import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  SafeAreaView,
  ScrollView,
  TextInput,
  Platform,
  useWindowDimensions,
  Modal,
  Switch,
  Pressable,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFocusEffect } from '@react-navigation/native';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import PostComposer from '../components/PostComposer';
import { fetchAllChessStats, calculateAverageRating, calculateWinRate } from '../services/chessAPIs';
import { getColors } from '../styles/theme';
import { fetchChesscomRatings, fetchLichessRatings, verifyChesscomUsername, verifyLichessUsername } from '../services/ChessRatingService';
import { getUserByClerkId, updateUser, getPostsByAuthor, getFriendsCount, getPendingRequestsCount, checkChessUsernameClaimed, unlinkChessAccount } from '../services/database';
import ClubHotspotPhotos from '../components/ClubHotspotPhotos';
import { getRecentSocialPosts, getSocialConnections, triggerSyncForClub } from '../services/socialSyncService';

// Responsive breakpoints
const DESKTOP_BREAKPOINT = 1024;
const TABLET_BREAKPOINT = 768;
const MAX_CONTENT_WIDTH = 680;
const MAX_WIDE_CONTENT_WIDTH = 900;

const SOCIAL_PROOF_VISIBLE_KEY = 'socialProofClubsVisible';
const SOCIAL_PROOF_CLUBS_KEY = 'socialProofClubs';
const DEFAULT_SOCIAL_PROOF_CLUBS = [
  { name: 'Regent Park Chess Club' },
  { name: 'Hart House (U of T)' },
  { name: 'Vancouver Beer Chess (launch partner)' },
];

const RecentInstagramPreview = ({ clubId, navigation, colors, isDarkMode }) => {
  const [posts, setIgPosts] = useState([]);
  const [hasConnection, setHasConnection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    (async () => {
      try {
        const [postsData, connections] = await Promise.all([
          getRecentSocialPosts(clubId, 6),
          getSocialConnections(clubId),
        ]);
        if (cancelled) return;
        setIgPosts(postsData?.posts ?? []);
        setHasConnection((connections || []).length > 0);
      } catch (e) {
        console.warn('IG preview fetch error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clubId]);

  const handleRefreshPosts = async () => {
    if (!clubId || refreshing) return;
    setRefreshing(true);
    try {
      await triggerSyncForClub(clubId);
      const { posts: postsData } = await getRecentSocialPosts(clubId, 6);
      setIgPosts(postsData || []);
      showToast('Posts refreshed', 'success');
    } catch (e) {
      console.warn('Refresh posts error:', e);
      showToast(e?.message || 'Refresh failed', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || (!hasConnection && posts.length === 0)) return null;

  return (
    <View style={{ marginBottom: 24 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingHorizontal: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Icon name="photo-library" size={20} color={colors.primary} />
          <Text style={{ fontSize: 16, fontWeight: '700', color: colors.text }}>Recent Posts</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <TouchableOpacity
            onPress={handleRefreshPosts}
            disabled={refreshing}
            activeOpacity={0.7}
          >
            <Text style={{ fontSize: 13, color: colors.primary, fontWeight: '600' }}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => navigation.navigate('YourClub', { initialTab: 'info' })}
            activeOpacity={0.7}
          >
            <Text style={{ fontSize: 13, color: colors.primary, fontWeight: '600' }}>Manage</Text>
          </TouchableOpacity>
        </View>
      </View>
      {posts.length === 0 ? (
        <TouchableOpacity
          onPress={() => navigation.navigate('YourClub', { initialTab: 'info' })}
          activeOpacity={0.7}
          style={{
            backgroundColor: isDarkMode ? '#1E2D1E' : '#F0F8F0',
            borderRadius: 12,
            padding: 20,
            alignItems: 'center',
            borderWidth: 1,
            borderColor: isDarkMode ? '#2D4A2D' : '#D0E8D0',
          }}
        >
          <Icon name="camera-alt" size={28} color={colors.textSecondary} />
          <Text style={{ fontSize: 14, color: colors.textSecondary, marginTop: 8, textAlign: 'center' }}>
            Connect Instagram to display recent posts
          </Text>
        </TouchableOpacity>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 12, paddingHorizontal: 2 }}>
          {posts.map((post) => {
            let payload = post.raw_payload || {};
            if (typeof payload === 'string') {
              try {
                payload = JSON.parse(payload);
              } catch {
                payload = {};
              }
            }
            // For video/reel, use only thumbnail_url; media_url is a video URL and cannot be used in <Image>
            const postType = (post.post_type || '').toString().toLowerCase();
            const isVideo = postType === 'video' || postType === 'reels';
            const imageUrl = isVideo
              ? (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) || null
              : (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                post.media_url;

            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Debug-Session-Id': '0bf1ae',
              },
              body: JSON.stringify({
                sessionId: '0bf1ae',
                location: 'ProfileScreen.js:RecentInstagramPreview',
                message: 'rendering IG preview post',
                data: {
                  postId: post.id,
                  postType: post.post_type,
                  hasRawPayload: !!post.raw_payload,
                  payloadHasThumbnail: !!(payload && payload.thumbnail_url),
                  payloadThumbnailSample:
                    payload && typeof payload.thumbnail_url === 'string'
                      ? payload.thumbnail_url.slice(0, 80)
                      : null,
                  mediaUrlSample:
                    typeof post.media_url === 'string' ? post.media_url.slice(0, 80) : null,
                  computedHasImageUrl: !!imageUrl,
                },
                timestamp: Date.now(),
                hypothesisId: 'T1',
              }),
            }).catch(() => {});
            // #endregion

            return (
              <TouchableOpacity
                key={post.id}
                activeOpacity={0.85}
                onPress={() => post.permalink && (Platform.OS === 'web' ? window.open(post.permalink, '_blank') : null)}
                style={{
                  width: 160,
                  borderRadius: 12,
                  overflow: 'hidden',
                  backgroundColor: isDarkMode ? '#2A2A2A' : '#F0F0F0',
                  ...(Platform.OS === 'web' ? { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } : { elevation: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4 }),
                }}
              >
                <View style={{ width: 160, height: 200 }}>
                  {imageUrl ? (
                    <Image source={{ uri: imageUrl }} style={{ width: 160, height: 200 }} resizeMode="cover" />
                  ) : (
                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Icon name={isVideo ? 'videocam' : 'image'} size={32} color={colors.textLight} />
                    </View>
                  )}
                </View>
                {post.caption ? (
                  <Text style={{ fontSize: 11, color: colors.textSecondary, paddingHorizontal: 8, paddingVertical: 8, lineHeight: 16 }} numberOfLines={2}>
                    {post.caption}
                  </Text>
                ) : null}
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
};

const ProfileScreen = ({ navigation }) => {
  const { user, signOut, appUser, clerkUserId, updateUserProfile } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const { width } = useWindowDimensions();
  
  // Responsive breakpoints
  const isWeb = Platform.OS === 'web';
  const isDesktop = isWeb && width >= DESKTOP_BREAKPOINT;
  const isTablet = isWeb && width >= TABLET_BREAKPOINT && width < DESKTOP_BREAKPOINT;
  const isLargeScreen = isDesktop || isTablet;
  
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [chessStats, setChessStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingChessStats, setLoadingChessStats] = useState(false);
  const [showChesscomInput, setShowChesscomInput] = useState(false);
  const [showLichessInput, setShowLichessInput] = useState(false);
  const [chesscomInput, setChesscomInput] = useState('');
  const [lichessInput, setLichessInput] = useState('');
  const [syncingChesscom, setSyncingChesscom] = useState(false);
  const [syncingLichess, setSyncingLichess] = useState(false);
  const [editingCity, setEditingCity] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [savingCity, setSavingCity] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [friendsCount, setFriendsCount] = useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  const [socialProofVisible, setSocialProofVisible] = useState(false);
  const [socialProofClubs, setSocialProofClubs] = useState(DEFAULT_SOCIAL_PROOF_CLUBS);
  const [showEditClubsModal, setShowEditClubsModal] = useState(false);
  const [newClubName, setNewClubName] = useState('');
  const [editClubsDraft, setEditClubsDraft] = useState([]);
  const [adminClubsSectionExpanded, setAdminClubsSectionExpanded] = useState(false); // Collapsed by default

  const [resubmitting, setResubmitting] = useState(false);

  // Always call latest fetchProfile from focus effect (avoid stale closure when clerkUserId loads later)
  const fetchProfileRef = useRef(() => {});

  useFocusEffect(
    React.useCallback(() => {
      fetchProfileRef.current?.();
    }, [clerkUserId])
  );

  // Refetch when clerkUserId becomes available (e.g. after redirect from sign-up)
  useEffect(() => {
    if (clerkUserId) fetchProfile();
  }, [clerkUserId]);

  // When auth gets profile picture (e.g. after sign-up sync), update local profile so it shows without refetch
  useEffect(() => {
    const missingPic = !profile?.profilePictureUrl || String(profile.profilePictureUrl).trim() === '';
    if (appUser?.profilePictureUrl && profile?.id === appUser?.id && missingPic) {
      setProfile((prev) => (prev ? { ...prev, profilePictureUrl: appUser.profilePictureUrl } : prev));
    }
  }, [appUser?.id, appUser?.profilePictureUrl, profile?.id, profile?.profilePictureUrl]);

  useEffect(() => {
    const loadSocialProof = async () => {
      try {
        let v = false;
        let c = DEFAULT_SOCIAL_PROOF_CLUBS;
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          v = localStorage.getItem(SOCIAL_PROOF_VISIBLE_KEY) === 'true';
          const raw = localStorage.getItem(SOCIAL_PROOF_CLUBS_KEY);
          if (raw) { try { c = JSON.parse(raw); } catch (_) {} }
        } else {
          v = (await AsyncStorage.getItem(SOCIAL_PROOF_VISIBLE_KEY)) === 'true';
          const raw = await AsyncStorage.getItem(SOCIAL_PROOF_CLUBS_KEY);
          if (raw) { try { c = JSON.parse(raw); } catch (_) {} }
        }
        setSocialProofVisible(v);
        setSocialProofClubs(Array.isArray(c) ? c : DEFAULT_SOCIAL_PROOF_CLUBS);
      } catch (e) { console.warn('Load social proof', e); }
    };
    loadSocialProof();
  }, []);

  const saveSocialProofVisible = async (val) => {
    setSocialProofVisible(val);
    try {
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        localStorage.setItem(SOCIAL_PROOF_VISIBLE_KEY, String(val));
      } else {
        await AsyncStorage.setItem(SOCIAL_PROOF_VISIBLE_KEY, String(val));
      }
      showToast(val ? 'Clubs section visible on Pricing page' : 'Clubs section hidden', 'success');
    } catch (e) { showToast('Failed to save', 'error'); }
  };

  const saveSocialProofClubs = (list) => {
    const toStore = Array.isArray(list) ? list : DEFAULT_SOCIAL_PROOF_CLUBS;
    setSocialProofClubs(toStore);
    const go = async () => {
      try {
        const s = JSON.stringify(toStore);
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          localStorage.setItem(SOCIAL_PROOF_CLUBS_KEY, s);
        } else {
          await AsyncStorage.setItem(SOCIAL_PROOF_CLUBS_KEY, s);
        }
        showToast('Clubs list saved', 'success');
      } catch (e) { showToast('Failed to save clubs', 'error'); }
    };
    go();
  };

  const openEditClubsModal = () => {
    setEditClubsDraft(socialProofClubs.map((x) => (typeof x === 'string' ? { name: x } : { ...x })));
    setNewClubName('');
    setShowEditClubsModal(true);
  };

  const addDraftClub = () => {
    const n = (newClubName || '').trim();
    if (!n) return;
    setEditClubsDraft((d) => [...d, { name: n }]);
    setNewClubName('');
  };

  const removeDraftClub = (idx) => {
    setEditClubsDraft((d) => d.filter((_, i) => i !== idx));
  };

  const closeEditClubsModal = (save) => {
    if (save) saveSocialProofClubs(editClubsDraft);
    setShowEditClubsModal(false);
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (!clerkUserId) {
        console.log('No clerkUserId available yet');
        setLoading(false);
        return;
      }
      
      const userProfile = await getUserByClerkId(clerkUserId);
      
      if (userProfile) {
        // Convert snake_case to camelCase for UI
        const formattedProfile = {
          id: userProfile.id,
          clerkId: userProfile.clerk_id,
          username: userProfile.username,
          email: userProfile.email,
          accountType: userProfile.account_type,
          isAdmin: userProfile.is_admin,
          profilePictureUrl: userProfile.profile_picture_url ?? appUser?.profilePictureUrl,
          rating: userProfile.rating,
          gamesPlayedInApp: userProfile.games_played_in_app,
          chesscomUsername: userProfile.chesscom_username,
          chesscomBlitz: userProfile.chesscom_blitz,
          chesscomRapid: userProfile.chesscom_rapid,
          chesscomClassical: userProfile.chesscom_classical,
          chesscomLastSync: userProfile.chesscom_last_sync,
          lichessUsername: userProfile.lichess_username,
          lichessBlitz: userProfile.lichess_blitz,
          lichessRapid: userProfile.lichess_rapid,
          lichessClassical: userProfile.lichess_classical,
          lichessLastSync: userProfile.lichess_last_sync,
          clubName: userProfile.club_name,
          address: userProfile.address,
          about: userProfile.about,
          website: userProfile.website,
          schedule: userProfile.schedule,
          tier: userProfile.tier,
          claimedBy: userProfile.claimed_by,
          city: userProfile.city,
          statusMessage: userProfile.status_message,
          isOnline: userProfile.is_online,
          lastOnlineAt: userProfile.last_online_at,
          // Approval fields for clubs
          approvalStatus: userProfile.approval_status,
          submissionPitch: userProfile.submission_pitch,
          submittedAt: userProfile.submitted_at,
          reviewedAt: userProfile.reviewed_at,
          rejectionReason: userProfile.rejection_reason,
          // Stripe fields for clubs
          stripeAccountId: userProfile.stripe_account_id,
          stripeOnboardingComplete: userProfile.stripe_onboarding_complete,
          memberListPublic: userProfile.member_list_public,
        };
        
        setProfile(formattedProfile);
        setCityInput(formattedProfile.city || '');
        setStatusInput(formattedProfile.statusMessage || '');

        // Update online status when viewing profile
        try {
          await updateUser(formattedProfile.id, {
            is_online: true,
            last_online_at: new Date().toISOString(),
          });
          // Update local profile state to reflect online status
          setProfile({ ...formattedProfile, isOnline: true, lastOnlineAt: new Date().toISOString() });
        } catch (error) {
          console.error('Error updating online status:', error);
          // Non-critical error, continue with profile load
        }

        // Fetch chess stats for players
        if (formattedProfile.accountType === 'PLAYER' && (formattedProfile.chesscomUsername || formattedProfile.lichessUsername)) {
          fetchChessStatsFunc(formattedProfile.chesscomUsername, formattedProfile.lichessUsername);
        }

        if (formattedProfile.accountType === 'CLUB') {
          fetchPostsFunc(formattedProfile.id);
        }
        
        // Fetch friends data for all users
        fetchFriendsData(formattedProfile.id);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showToast('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  fetchProfileRef.current = fetchProfile;

  const fetchFriendsData = async (userId) => {
    try {
      const [friendsTotal, pendingTotal] = await Promise.all([
        getFriendsCount(userId),
        getPendingRequestsCount(userId),
      ]);
      setFriendsCount(friendsTotal);
      setPendingRequestsCount(pendingTotal);
    } catch (error) {
      console.error('Error fetching friends data:', error);
    }
  };

  const fetchChessStatsFunc = async (chesscomUsername, lichessUsername) => {
    try {
      setLoadingChessStats(true);
      const stats = await fetchAllChessStats(chesscomUsername, lichessUsername);
      setChessStats(stats);
    } catch (error) {
      console.error('Error fetching chess stats:', error);
    } finally {
      setLoadingChessStats(false);
    }
  };

  const fetchPostsFunc = async (authorId) => {
    try {
      const postsData = await getPostsByAuthor(authorId);
      // Convert to camelCase
      const formattedPosts = postsData.map(p => ({
        id: p.id,
        content: p.content,
        createdAt: p.created_at,
      }));
      setPosts(formattedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };


  // Handle resubmitting club application
  const handleResubmitApplication = async () => {
    if (!profile?.id) return;

    const confirmed = Platform.OS === 'web'
      ? window.confirm('Resubmit your club application? This will update your submission date and notify our team.')
      : await new Promise((resolve) => {
          Alert.alert(
            'Resubmit Application',
            'Resubmit your club application? This will update your submission date and notify our team.',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Resubmit', style: 'default', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmed) return;

    setResubmitting(true);
    try {
      const now = new Date().toISOString();
      await updateUser(profile.id, {
        submitted_at: now,
        last_submission_attempt: now,
        approval_status: 'PENDING',
        reviewed_at: null,
        reviewed_by: null,
        rejection_reason: null,
      });

      // Update local profile state
      setProfile(prev => ({
        ...prev,
        submittedAt: now,
        approvalStatus: 'PENDING',
        reviewedAt: null,
        rejectionReason: null,
      }));

      // Send admin notification
      try {
        const { sendAdminNotificationEmail } = require('../services/emailService');
        await sendAdminNotificationEmail({
          clubName: profile.clubName || profile.username,
          email: profile.email,
          pitch: profile.submissionPitch || 'No pitch provided',
          address: profile.address || 'No address provided',
          socialLinks: {
            website: profile.website || null,
            instagram: profile.instagramUrl || null,
            twitter: profile.twitterUrl || null,
            facebook: profile.facebookUrl || null,
          },
          isResubmission: true,
        });
      } catch (emailError) {
        console.error('Failed to send admin notification:', emailError);
        // Don't block resubmission if email fails
      }

      showToast('Application resubmitted successfully! Our team will review it shortly.', 'success');
    } catch (error) {
      console.error('Error resubmitting application:', error);
      showToast('Failed to resubmit application', 'error');
    } finally {
      setResubmitting(false);
    }
  };

  const handleSignOut = async () => {
    console.log('🚪 Sign out button pressed');
    
    // On web, use confirm instead of Alert.alert
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Are you sure you want to sign out?');
      console.log('Confirmation result:', confirmed);
      
      if (confirmed) {
        console.log('User confirmed, signing out...');
        const result = await signOut();
        if (!result.success) {
          showToast('Sign out failed: ' + result.error, 'error');
        }
      }
    } else {
      // Native app
      Alert.alert(
        'Sign Out',
        'Are you sure you want to sign out?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Sign Out',
            style: 'destructive',
            onPress: async () => {
              const result = await signOut();
              if (!result.success) {
                showToast('Sign out failed: ' + result.error, 'error');
              }
            },
          },
        ]
      );
    }
  };

  const handleProfilePictureUpload = async (imageKey) => {
    console.log('📸 Profile picture uploaded, updating database...');
    try {
      const updateResult = await updateUserProfile({ profilePictureUrl: imageKey });
      
      if (!updateResult?.success) {
        throw new Error(updateResult?.error || 'Database update failed');
      }
      
      // Update local state immediately
      setProfile({ ...profile, profilePictureUrl: imageKey });
      showToast('Profile picture updated!', 'success');
      console.log('✅ Profile picture updated in database');
    } catch (error) {
      console.error('❌ Failed to update profile picture in database:', error);
      showToast('Upload succeeded but database update failed', 'error');
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLinkChesscom = async () => {
    if (!chesscomInput.trim()) {
      showToast('Please enter a Chess.com username', 'error');
      return;
    }

    try {
      setSyncingChesscom(true);

      // Verify username exists on Chess.com
      const isValid = await verifyChesscomUsername(chesscomInput.trim());
      if (!isValid) {
        showToast('Chess.com username not found', 'error');
        return;
      }

      // Check if this Chess.com account is already claimed by another user
      const existingClaim = await checkChessUsernameClaimed('chesscom', chesscomInput.trim(), profile.id);
      if (existingClaim) {
        showToast(`This Chess.com account is already linked to user "${existingClaim.username}"`, 'error');
        return;
      }

      // Fetch ratings
      const ratings = await fetchChesscomRatings(chesscomInput.trim());

      // Update profile using Supabase
      await updateUser(profile.id, {
        chesscom_username: chesscomInput.trim(),
        chesscom_blitz: ratings.blitz,
        chesscom_rapid: ratings.rapid,
        chesscom_classical: ratings.classical,
        chesscom_last_sync: new Date().toISOString(),
      });

      showToast('Chess.com account linked successfully!', 'success');
      setShowChesscomInput(false);
      setChesscomInput('');
      await fetchProfile();
    } catch (error) {
      console.error('Error linking Chess.com:', error);
      // Handle unique constraint violation from database
      if (error.code === '23505') {
        showToast('This Chess.com account is already linked to another user', 'error');
      } else {
        showToast(error.message || 'Failed to link Chess.com account', 'error');
      }
    } finally {
      setSyncingChesscom(false);
    }
  };

  const handleLinkLichess = async () => {
    if (!lichessInput.trim()) {
      showToast('Please enter a Lichess username', 'error');
      return;
    }

    try {
      setSyncingLichess(true);

      // Verify username exists on Lichess
      const isValid = await verifyLichessUsername(lichessInput.trim());
      if (!isValid) {
        showToast('Lichess username not found', 'error');
        return;
      }

      // Check if this Lichess account is already claimed by another user
      const existingClaim = await checkChessUsernameClaimed('lichess', lichessInput.trim(), profile.id);
      if (existingClaim) {
        showToast(`This Lichess account is already linked to user "${existingClaim.username}"`, 'error');
        return;
      }

      // Fetch ratings
      const ratings = await fetchLichessRatings(lichessInput.trim());

      // Update profile using Supabase
      await updateUser(profile.id, {
        lichess_username: lichessInput.trim(),
        lichess_blitz: ratings.blitz,
        lichess_rapid: ratings.rapid,
        lichess_classical: ratings.classical,
        lichess_last_sync: new Date().toISOString(),
      });

      showToast('Lichess account linked successfully!', 'success');
      setShowLichessInput(false);
      setLichessInput('');
      await fetchProfile();
    } catch (error) {
      console.error('Error linking Lichess:', error);
      // Handle unique constraint violation from database
      if (error.code === '23505') {
        showToast('This Lichess account is already linked to another user', 'error');
      } else {
        showToast(error.message || 'Failed to link Lichess account', 'error');
      }
    } finally {
      setSyncingLichess(false);
    }
  };

  const handleSyncChesscom = async () => {
    if (!profile.chesscomUsername) return;

    try {
      setSyncingChesscom(true);
      const ratings = await fetchChesscomRatings(profile.chesscomUsername);

      await updateUser(profile.id, {
        chesscom_blitz: ratings.blitz,
        chesscom_rapid: ratings.rapid,
        chesscom_classical: ratings.classical,
        chesscom_last_sync: new Date().toISOString(),
      });

      showToast('Chess.com ratings synced!', 'success');
      await fetchProfile();
    } catch (error) {
      console.error('Error syncing Chess.com:', error);
      showToast('Failed to sync Chess.com ratings', 'error');
    } finally {
      setSyncingChesscom(false);
    }
  };

  const handleSyncLichess = async () => {
    if (!profile.lichessUsername) return;

    try {
      setSyncingLichess(true);
      const ratings = await fetchLichessRatings(profile.lichessUsername);

      await updateUser(profile.id, {
        lichess_blitz: ratings.blitz,
        lichess_rapid: ratings.rapid,
        lichess_classical: ratings.classical,
        lichess_last_sync: new Date().toISOString(),
      });

      showToast('Lichess ratings synced!', 'success');
      await fetchProfile();
    } catch (error) {
      console.error('Error syncing Lichess:', error);
      showToast('Failed to sync Lichess ratings', 'error');
    } finally {
      setSyncingLichess(false);
    }
  };

  const handleUnlinkChesscom = async () => {
    Alert.alert(
      'Unlink Chess.com Account',
      `Are you sure you want to unlink @${profile.chesscomUsername}? Your ratings will no longer be synced.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unlink',
          style: 'destructive',
          onPress: async () => {
            try {
              setSyncingChesscom(true);
              await unlinkChessAccount(profile.id, 'chesscom');
              showToast('Chess.com account unlinked', 'success');
              await fetchProfile();
            } catch (error) {
              console.error('Error unlinking Chess.com:', error);
              showToast('Failed to unlink Chess.com account', 'error');
            } finally {
              setSyncingChesscom(false);
            }
          },
        },
      ]
    );
  };

  const handleUnlinkLichess = async () => {
    Alert.alert(
      'Unlink Lichess Account',
      `Are you sure you want to unlink @${profile.lichessUsername}? Your ratings will no longer be synced.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unlink',
          style: 'destructive',
          onPress: async () => {
            try {
              setSyncingLichess(true);
              await unlinkChessAccount(profile.id, 'lichess');
              showToast('Lichess account unlinked', 'success');
              await fetchProfile();
            } catch (error) {
              console.error('Error unlinking Lichess:', error);
              showToast('Failed to unlink Lichess account', 'error');
            } finally {
              setSyncingLichess(false);
            }
          },
        },
      ]
    );
  };

  const handleSaveCity = async () => {
    try {
      setSavingCity(true);
      await updateUser(profile.id, { city: cityInput.trim() || null });

      setProfile({ ...profile, city: cityInput.trim() || null });
      setEditingCity(false);
      showToast('City updated!', 'success');
    } catch (error) {
      console.error('Error updating city:', error);
      showToast('Failed to update city', 'error');
    } finally {
      setSavingCity(false);
    }
  };

  const handleSaveStatus = async () => {
    try {
      setSavingStatus(true);
      await updateUser(profile.id, { status_message: statusInput.trim() || null });

      setProfile({ ...profile, statusMessage: statusInput.trim() || null });
      setEditingStatus(false);
      showToast('Status updated!', 'success');
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Failed to update status', 'error');
    } finally {
      setSavingStatus(false);
    }
  };

  // Dynamic styles based on theme and screen size
  const dynamicStyles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
    },
    // Responsive content wrapper
    contentWrapper: {
      width: '100%',
      maxWidth: isDesktop ? MAX_CONTENT_WIDTH : '100%',
      alignSelf: 'center',
      paddingHorizontal: isDesktop ? 0 : 16,
    },
    // Wide content wrapper for two-column layouts
    wideContentWrapper: {
      width: '100%',
      maxWidth: isDesktop ? MAX_WIDE_CONTENT_WIDTH : '100%',
      alignSelf: 'center',
      paddingHorizontal: isDesktop ? 0 : 16,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: isDesktop ? 24 : 16,
      marginBottom: 16,
    },
    sectionTitle: {
      fontSize: isDesktop ? 20 : 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    statItem: {
      flex: 1,
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9',
      padding: 16,
      borderRadius: 12,
      alignItems: 'center',
    },
    platformStats: {
      marginBottom: 12,
      padding: 12,
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9',
      borderRadius: 8,
    },
    overallStats: {
      padding: 12,
      backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9',
      borderRadius: 8,
      alignItems: 'center',
    },
    postCard: {
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9',
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
    },
    eventCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9',
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
    },
    adminButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : '#FFF3E0',
      padding: 16,
      borderRadius: 12,
      gap: 12,
      marginBottom: 8,
    },
    statusBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : '#FFF3E0',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      marginRight: 8,
    },
    statusBadgePro: {
      backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9',
    },
    // Desktop-specific layouts
    twoColumnRow: {
      flexDirection: isDesktop ? 'row' : 'column',
      gap: 16,
    },
    columnHalf: {
      flex: isDesktop ? 1 : undefined,
      width: isDesktop ? undefined : '100%',
    },
    // Profile header responsive styling
    profileHeader: {
      alignItems: 'center',
      paddingVertical: isDesktop ? 32 : 20,
      paddingHorizontal: isDesktop ? 40 : 16,
      maxWidth: isDesktop ? MAX_CONTENT_WIDTH : '100%',
      alignSelf: 'center',
      width: '100%',
    },
  };

  if (loading) {
    return (
      <View style={dynamicStyles.loadingContainer}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!profile) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, { color: colors.text }]}>Profile not found</Text>
        <Text style={[styles.errorSubtext, { color: colors.textSecondary }]}>Please try logging out and back in</Text>
        <TouchableOpacity style={styles.signOutButton} onPress={handleSignOut}>
          <Text style={styles.signOutButtonText}>Sign Out</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Club accounts that are not yet approved have restricted functionality
  const isClubPendingApproval = profile.accountType === 'CLUB' &&
    profile.approvalStatus && profile.approvalStatus !== 'APPROVED';

  return (
    <SafeAreaView style={dynamicStyles.container}>
      {/* Header with Settings Button */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={[styles.headerContent, isDesktop && styles.headerContentDesktop]}>
          <Text style={[styles.headerTitle, { color: colors.text }]}>Profile</Text>
          <TouchableOpacity 
            style={styles.settingsButton}
            onPress={() => navigation.navigate('Settings')}
          >
            <Icon name="settings" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView 
        contentContainerStyle={[
          styles.content,
          isDesktop && styles.contentDesktop
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Profile Header Section - Constrained width */}
        <View style={dynamicStyles.profileHeader}>
          {/* Profile Picture */}
          <ProfilePictureUpload
            userId={profile.id}
            currentImageUrl={profile.profilePictureUrl}
            onUploadComplete={handleProfilePictureUpload}
          />

          {/* Username/Club Name */}
          <Text style={[styles.username, { color: colors.text }]}>
            {profile.accountType === 'CLUB' ? profile.clubName : profile.username}
          </Text>
          <Text style={[styles.email, { color: colors.textSecondary }]}>{profile.email}</Text>
          <View style={[styles.accountTypeBadge, { backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9' }]}>
            <Text style={[styles.accountTypeText, { color: colors.primary }]}>
              {profile.accountType === 'CLUB' ? '🏛️ Club' : '♟️ Player'}
            </Text>
          </View>

          {/* Online Status Indicator */}
          <View style={styles.onlineStatusContainer}>
            <View style={[styles.onlineIndicator, { backgroundColor: profile.isOnline ? '#4CAF50' : '#9E9E9E' }]} />
            <Text style={[styles.onlineStatusText, { color: colors.textSecondary }]}>
              {profile.isOnline ? 'Online Now' : 'Offline'}
            </Text>
          </View>
        </View>

        {/* Main Content - Constrained Width */}
        <View style={dynamicStyles.contentWrapper}>
          {/* Friends Section */}
          <TouchableOpacity 
            style={[dynamicStyles.section, styles.friendsSection]}
            onPress={() => navigation.navigate('Friends')}
          >
            <View style={styles.friendsSectionContent}>
              <View style={styles.friendsLeft}>
                <View style={[styles.friendsIconCircle, { backgroundColor: isDarkMode ? 'rgba(148, 235, 152, 0.15)' : 'rgba(148, 235, 152, 0.2)' }]}>
                  <Icon name="people" size={24} color={colors.primary} />
                </View>
                <View style={styles.friendsTextContainer}>
                  <Text style={[styles.friendsTitle, { color: colors.text }]}>Friends</Text>
                  <Text style={[styles.friendsSubtitle, { color: colors.textSecondary }]}>
                    {friendsCount} {friendsCount === 1 ? 'friend' : 'friends'}
                  </Text>
                </View>
              </View>
              <View style={styles.friendsRight}>
                {pendingRequestsCount > 0 && (
                  <View style={styles.pendingBadge}>
                    <Text style={styles.pendingBadgeText}>{pendingRequestsCount}</Text>
                  </View>
                )}
                <Icon name="chevron-right" size={24} color={colors.textLight} />
              </View>
            </View>
            {pendingRequestsCount > 0 && (
              <TouchableOpacity
                style={[styles.pendingRequestsBar, { backgroundColor: isDarkMode ? 'rgba(148, 235, 152, 0.1)' : 'rgba(148, 235, 152, 0.15)' }]}
                onPress={() => navigation.navigate('Friends', { initialTab: 'requests' })}
                activeOpacity={0.85}
              >
                <Icon name="person-add" size={16} color={colors.primary} />
                <Text style={[styles.pendingRequestsText, { color: colors.primary }]}>
                  {pendingRequestsCount} pending friend {pendingRequestsCount === 1 ? 'request' : 'requests'}
                </Text>
              </TouchableOpacity>
            )}
          </TouchableOpacity>

          {/* City & Status Section */}
          <View style={dynamicStyles.section}>
            <Text style={dynamicStyles.sectionTitle}>About Me</Text>
            
            {/* Two-column layout for desktop */}
            <View style={dynamicStyles.twoColumnRow}>
              {/* City */}
              <View style={[styles.profileFieldContainer, dynamicStyles.columnHalf]}>
                <View style={styles.profileFieldHeader}>
                  <View style={styles.profileFieldLabelRow}>
                    <Icon name="location-city" size={18} color={colors.textSecondary} />
                    <Text style={[styles.profileFieldLabel, { color: colors.textSecondary }]}>City</Text>
                  </View>
                  {!editingCity && !isClubPendingApproval && (
                    <TouchableOpacity
                      style={styles.editFieldButton}
                      onPress={() => setEditingCity(true)}
                    >
                      <Icon name="edit" size={16} color={colors.primary} />
                    </TouchableOpacity>
                  )}
                </View>
                {editingCity ? (
                  <View style={styles.editFieldContainer}>
                    <TextInput
                      style={[styles.fieldInput, { color: colors.text, borderColor: colors.border, backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}
                      placeholder="Enter your city"
                      placeholderTextColor={colors.textLight}
                      value={cityInput}
                      onChangeText={setCityInput}
                      autoCapitalize="words"
                    />
                    <View style={styles.editFieldButtons}>
                      <TouchableOpacity
                        style={[styles.saveFieldButton, { backgroundColor: colors.primary }]}
                        onPress={handleSaveCity}
                        disabled={savingCity}
                      >
                        {savingCity ? (
                          <LoadingAnimation size={30} />
                        ) : (
                          <Text style={styles.saveFieldButtonText}>Save</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.cancelFieldButton}
                        onPress={() => {
                          setEditingCity(false);
                          setCityInput(profile.city || '');
                        }}
                      >
                        <Text style={[styles.cancelFieldButtonText, { color: colors.textSecondary }]}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <Text style={[styles.profileFieldValue, { color: profile.city ? colors.text : colors.textLight }]}>
                    {profile.city || 'Not set'}
                  </Text>
                )}
              </View>

              {/* Status */}
              <View style={[styles.profileFieldContainer, dynamicStyles.columnHalf, !isDesktop && { marginTop: 16 }]}>
                <View style={styles.profileFieldHeader}>
                  <View style={styles.profileFieldLabelRow}>
                    <Icon name="chat-bubble-outline" size={18} color={colors.textSecondary} />
                    <Text style={[styles.profileFieldLabel, { color: colors.textSecondary }]}>Status</Text>
                  </View>
                  {!editingStatus && !isClubPendingApproval && (
                    <TouchableOpacity
                      style={styles.editFieldButton}
                      onPress={() => setEditingStatus(true)}
                    >
                      <Icon name="edit" size={16} color={colors.primary} />
                    </TouchableOpacity>
                  )}
                </View>
                {editingStatus ? (
                  <View style={styles.editFieldContainer}>
                    <TextInput
                      style={[styles.fieldInput, styles.statusInput, { color: colors.text, borderColor: colors.border, backgroundColor: isDarkMode ? colors.backgroundDark : '#f9f9f9' }]}
                      placeholder="What's on your mind?"
                      placeholderTextColor={colors.textLight}
                      value={statusInput}
                      onChangeText={setStatusInput}
                      multiline
                      maxLength={100}
                    />
                    <Text style={[styles.characterCount, { color: colors.textLight }]}>{statusInput.length}/100</Text>
                    <View style={styles.editFieldButtons}>
                      <TouchableOpacity
                        style={[styles.saveFieldButton, { backgroundColor: colors.primary }]}
                        onPress={handleSaveStatus}
                        disabled={savingStatus}
                      >
                        {savingStatus ? (
                          <LoadingAnimation size={30} />
                        ) : (
                          <Text style={styles.saveFieldButtonText}>Save</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.cancelFieldButton}
                        onPress={() => {
                          setEditingStatus(false);
                          setStatusInput(profile.statusMessage || '');
                        }}
                      >
                        <Text style={[styles.cancelFieldButtonText, { color: colors.textSecondary }]}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <Text style={[styles.profileFieldValue, styles.statusValue, { color: profile.statusMessage ? colors.text : colors.textLight }]}>
                    {profile.statusMessage || 'No status set'}
                  </Text>
                )}
              </View>
            </View>
          </View>

          {/* ChessMaps Pro - club accounts only (Pro features are for clubs) */}
          {profile.accountType === 'CLUB' && (
            <View style={{ marginTop: 16, marginBottom: 24 }}>
              <TouchableOpacity
                style={[styles.pricingButton, { backgroundColor: colors.primary }]}
                onPress={() => navigation.navigate('Pricing')}
              >
                <Icon name="star" size={20} color="white" />
                <Text style={styles.pricingButtonText}>ChessMaps Pro</Text>
              </TouchableOpacity>
            </View>
          )}


          {/* Player Profile - Dashboard Style */}
          {profile.accountType === 'PLAYER' && (
            <>
              {/* Stats Dashboard */}
              <View style={[styles.dashboardContainer, { backgroundColor: colors.surface }]}>
                {/* Desktop: Horizontal layout for main stat + secondary stats */}
                <View style={isDesktop ? styles.dashboardRowDesktop : undefined}>
                  {/* Main Rating Card */}
                  <View style={[
                    styles.mainStatCard, 
                    { backgroundColor: isDarkMode ? 'rgba(148, 235, 152, 0.1)' : 'rgba(148, 235, 152, 0.15)' },
                    isDesktop && styles.mainStatCardDesktop
                  ]}>
                    <View style={styles.mainStatIcon}>
                      <Icon name="stars" size={28} color={colors.primary} />
                    </View>
                    <Text style={[styles.mainStatValue, { color: colors.text }]}>{profile.rating || 1200}</Text>
                    <Text style={[styles.mainStatLabel, { color: colors.textSecondary }]}>In-App Rating</Text>
                  </View>

                  {/* Secondary Stats Row */}
                  <View style={[styles.secondaryStatsRow, isDesktop && styles.secondaryStatsRowDesktop]}>
                    <View style={[styles.secondaryStatCard, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5' }]}>
                      <Icon name="sports-esports" size={22} color={colors.primary} />
                      <Text style={[styles.secondaryStatValue, { color: colors.text }]}>{profile.gamesPlayedInApp || 0}</Text>
                      <Text style={[styles.secondaryStatLabel, { color: colors.textSecondary }]}>Games Played</Text>
                    </View>
                    <View style={[styles.secondaryStatCard, { backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5' }]}>
                      <Icon name="trending-up" size={22} color="#4CAF50" />
                      <Text style={[styles.secondaryStatValue, { color: colors.text }]}>
                        {chessStats ? `${calculateWinRate(chessStats)}%` : '—'}
                      </Text>
                      <Text style={[styles.secondaryStatLabel, { color: colors.textSecondary }]}>Win Rate</Text>
                    </View>
                  </View>
                </View>
              </View>

              {/* Imported Ratings Dashboard */}
              <View style={[styles.dashboardContainer, { backgroundColor: colors.surface }]}>
                <View style={styles.dashboardHeader}>
                  <Text style={[styles.dashboardTitle, { color: colors.text }]}>Imported Ratings</Text>
                  <Text style={[styles.dashboardSubtitle, { color: colors.textSecondary }]}>
                    Connect your chess accounts
                  </Text>
                </View>

                {/* Platform Cards - Two columns on desktop */}
                <View style={isDesktop ? styles.platformCardsRowDesktop : undefined}>
                  {/* Chess.com Card */}
                  <View style={[
                    styles.platformCard, 
                    { 
                      backgroundColor: isDarkMode ? 'rgba(129, 183, 96, 0.08)' : 'rgba(129, 183, 96, 0.1)',
                      borderColor: profile.chesscomUsername ? colors.primary : 'transparent',
                      borderWidth: profile.chesscomUsername ? 1 : 0,
                    },
                    isDesktop && styles.platformCardDesktop,
                    !isDesktop && styles.platformCardMobile,
                  ]}>
                <View style={styles.platformCardHeader}>
                  <View style={[styles.platformBrand, isDesktop && styles.platformBrandDesktop]}>
                    <Text style={styles.platformIcon}>♟️</Text>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={[styles.platformName, { color: colors.text }]} numberOfLines={1}>Chess.com</Text>
                      {profile.chesscomUsername && (
                        <Text style={[styles.platformUserHandle, { color: colors.textSecondary }]} numberOfLines={1}>
                          @{profile.chesscomUsername}
                        </Text>
                      )}
                    </View>
                  </View>
                  {!profile.chesscomUsername ? (
                    <TouchableOpacity
                      style={[styles.connectBtn, { backgroundColor: colors.primary }]}
                      onPress={() => setShowChesscomInput(!showChesscomInput)}
                    >
                      <Icon name="add" size={18} color="#fff" />
                      <Text style={styles.connectBtnText}>Connect</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.platformCardHeaderActions}>
                      <TouchableOpacity
                        style={[styles.syncBtn, { borderColor: colors.primary }]}
                        onPress={handleSyncChesscom}
                        disabled={syncingChesscom}
                      >
                        {syncingChesscom ? (
                          <LoadingAnimation size={32} />
                        ) : (
                          <Icon name="sync" size={18} color={colors.primary} />
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.syncBtn, { borderColor: colors.error || '#E53935' }]}
                        onPress={handleUnlinkChesscom}
                        disabled={syncingChesscom}
                      >
                        <Icon name="link-off" size={18} color={colors.error || '#E53935'} />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>

                {showChesscomInput && (
                  <View style={styles.connectInputContainer}>
                    <TextInput
                      style={[styles.connectInput, { 
                        color: colors.text, 
                        backgroundColor: isDarkMode ? colors.background : '#fff',
                        borderColor: colors.border 
                      }]}
                      placeholder="Enter your Chess.com username"
                      placeholderTextColor={colors.textLight}
                      value={chesscomInput}
                      onChangeText={setChesscomInput}
                      autoCapitalize="none"
                    />
                    <View style={styles.connectInputActions}>
                      <TouchableOpacity
                        style={[styles.connectConfirmBtn, { backgroundColor: colors.primary }]}
                        onPress={handleLinkChesscom}
                        disabled={syncingChesscom}
                      >
                        {syncingChesscom ? (
                          <LoadingAnimation size={32} />
                        ) : (
                          <Text style={styles.connectConfirmText}>Link Account</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.connectCancelBtn}
                        onPress={() => { setShowChesscomInput(false); setChesscomInput(''); }}
                      >
                        <Text style={[styles.connectCancelText, { color: colors.textSecondary }]}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                {profile.chesscomUsername && (profile.chesscomBlitz || profile.chesscomRapid || profile.chesscomClassical) && (
                  <View style={styles.ratingCardsRow}>
                    {profile.chesscomBlitz && (
                      <View style={[styles.ratingMiniCard, { backgroundColor: isDarkMode ? colors.background : '#fff' }]}>
                        <Icon name="flash-on" size={16} color="#FFC107" />
                        <Text style={[styles.ratingMiniValue, { color: colors.text }]}>{profile.chesscomBlitz}</Text>
                        <Text style={[styles.ratingMiniLabel, { color: colors.textSecondary }]}>Blitz</Text>
                      </View>
                    )}
                    {profile.chesscomRapid && (
                      <View style={[styles.ratingMiniCard, { backgroundColor: isDarkMode ? colors.background : '#fff' }]}>
                        <Icon name="speed" size={16} color="#2196F3" />
                        <Text style={[styles.ratingMiniValue, { color: colors.text }]}>{profile.chesscomRapid}</Text>
                        <Text style={[styles.ratingMiniLabel, { color: colors.textSecondary }]}>Rapid</Text>
                      </View>
                    )}
                    {profile.chesscomClassical && (
                      <View style={[styles.ratingMiniCard, { backgroundColor: isDarkMode ? colors.background : '#fff' }]}>
                        <Icon name="schedule" size={16} color="#9C27B0" />
                        <Text style={[styles.ratingMiniValue, { color: colors.text }]}>{profile.chesscomClassical}</Text>
                        <Text style={[styles.ratingMiniLabel, { color: colors.textSecondary }]}>Daily</Text>
                      </View>
                    )}
                  </View>
                )}
                {profile.chesscomLastSync && (
                  <Text style={[styles.syncTimestamp, { color: colors.textLight }]}>
                    Synced {formatDate(profile.chesscomLastSync)}
                  </Text>
                )}
              </View>

                  {/* Lichess Card */}
                  <View style={[
                    styles.platformCard, 
                    { 
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                      borderColor: profile.lichessUsername ? '#fff' : 'transparent',
                      borderWidth: profile.lichessUsername ? 1 : 0,
                    },
                    isDesktop && styles.platformCardDesktop,
                    !isDesktop && styles.platformCardMobile,
                  ]}>
                <View style={styles.platformCardHeader}>
                  <View style={[styles.platformBrand, isDesktop && styles.platformBrandDesktop]}>
                    <Text style={styles.platformIcon}>⚡</Text>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={[styles.platformName, { color: colors.text }]} numberOfLines={1}>Lichess</Text>
                      {profile.lichessUsername && (
                        <Text style={[styles.platformUserHandle, { color: colors.textSecondary }]} numberOfLines={1}>
                          @{profile.lichessUsername}
                        </Text>
                      )}
                    </View>
                  </View>
                  {!profile.lichessUsername ? (
                    <TouchableOpacity
                      style={[styles.connectBtn, { backgroundColor: isDarkMode ? '#fff' : '#333' }]}
                      onPress={() => setShowLichessInput(!showLichessInput)}
                    >
                      <Icon name="add" size={18} color={isDarkMode ? '#333' : '#fff'} />
                      <Text style={[styles.connectBtnText, { color: isDarkMode ? '#333' : '#fff' }]}>Connect</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.platformCardHeaderActions}>
                      <TouchableOpacity
                        style={[styles.syncBtn, { borderColor: isDarkMode ? '#fff' : '#333' }]}
                        onPress={handleSyncLichess}
                        disabled={syncingLichess}
                      >
                        {syncingLichess ? (
                          <LoadingAnimation size={32} />
                        ) : (
                          <Icon name="sync" size={18} color={isDarkMode ? '#fff' : '#333'} />
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.syncBtn, { borderColor: colors.error || '#E53935' }]}
                        onPress={handleUnlinkLichess}
                        disabled={syncingLichess}
                      >
                        <Icon name="link-off" size={18} color={colors.error || '#E53935'} />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>

                {showLichessInput && (
                  <View style={styles.connectInputContainer}>
                    <TextInput
                      style={[styles.connectInput, { 
                        color: colors.text, 
                        backgroundColor: isDarkMode ? colors.background : '#fff',
                        borderColor: colors.border 
                      }]}
                      placeholder="Enter your Lichess username"
                      placeholderTextColor={colors.textLight}
                      value={lichessInput}
                      onChangeText={setLichessInput}
                      autoCapitalize="none"
                    />
                    <View style={styles.connectInputActions}>
                      <TouchableOpacity
                        style={[styles.connectConfirmBtn, { backgroundColor: isDarkMode ? '#fff' : '#333' }]}
                        onPress={handleLinkLichess}
                        disabled={syncingLichess}
                      >
                        {syncingLichess ? (
                          <LoadingAnimation size={32} />
                        ) : (
                          <Text style={[styles.connectConfirmText, { color: isDarkMode ? '#333' : '#fff' }]}>Link Account</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.connectCancelBtn}
                        onPress={() => { setShowLichessInput(false); setLichessInput(''); }}
                      >
                        <Text style={[styles.connectCancelText, { color: colors.textSecondary }]}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                {profile.lichessUsername && (profile.lichessBlitz || profile.lichessRapid || profile.lichessClassical) && (
                  <View style={styles.ratingCardsRow}>
                    {profile.lichessBlitz && (
                      <View style={[styles.ratingMiniCard, { backgroundColor: isDarkMode ? colors.background : '#fff' }]}>
                        <Icon name="flash-on" size={16} color="#FFC107" />
                        <Text style={[styles.ratingMiniValue, { color: colors.text }]}>{profile.lichessBlitz}</Text>
                        <Text style={[styles.ratingMiniLabel, { color: colors.textSecondary }]}>Blitz</Text>
                      </View>
                    )}
                    {profile.lichessRapid && (
                      <View style={[styles.ratingMiniCard, { backgroundColor: isDarkMode ? colors.background : '#fff' }]}>
                        <Icon name="speed" size={16} color="#2196F3" />
                        <Text style={[styles.ratingMiniValue, { color: colors.text }]}>{profile.lichessRapid}</Text>
                        <Text style={[styles.ratingMiniLabel, { color: colors.textSecondary }]}>Rapid</Text>
                      </View>
                    )}
                    {profile.lichessClassical && (
                      <View style={[styles.ratingMiniCard, { backgroundColor: isDarkMode ? colors.background : '#fff' }]}>
                        <Icon name="hourglass-empty" size={16} color="#9C27B0" />
                        <Text style={[styles.ratingMiniValue, { color: colors.text }]}>{profile.lichessClassical}</Text>
                        <Text style={[styles.ratingMiniLabel, { color: colors.textSecondary }]}>Classical</Text>
                      </View>
                    )}
                  </View>
                )}
                  {profile.lichessLastSync && (
                    <Text style={[styles.syncTimestamp, { color: colors.textLight }]}>
                      Synced {formatDate(profile.lichessLastSync)}
                    </Text>
                  )}
                  </View>
                </View>
              </View>
            </>
          )}

          {/* Club Profile */}
          {profile.accountType === 'CLUB' && (
            <>
              {/* Approval Status Section */}
              {profile.approvalStatus && (
                <View style={dynamicStyles.section}>
                  {profile.approvalStatus === 'APPROVED' ? (
                    // Approved Status
                    <View style={[styles.approvalStatusContainer, styles.approvalStatusApproved]}>
                      <View style={styles.approvalStatusHeader}>
                        <Icon name="check-circle" size={24} color="#4CAF50" />
                        <Text style={[styles.approvalStatusTitle, { color: '#4CAF50' }]}>
                          Club Approved! 🎉
                        </Text>
                      </View>
                      <Text style={[styles.approvalStatusText, { color: colors.textSecondary }]}>
                        Your club has been approved and is now live on ChessMaps. You can now create events, manage members, and accept payments.
                      </Text>
                      {profile.reviewedAt && (
                        <Text style={styles.submittedAtText}>
                          Approved: {new Date(profile.reviewedAt).toLocaleDateString()}
                        </Text>
                      )}
                    </View>
                  ) : (
                    // Pending or Rejected Status
                    <View style={[
                      styles.approvalStatusContainer,
                      profile.approvalStatus === 'PENDING' && styles.approvalStatusPending,
                      profile.approvalStatus === 'REJECTED' && styles.approvalStatusRejected,
                    ]}>
                      <View style={styles.approvalStatusHeader}>
                        <Icon 
                          name={profile.approvalStatus === 'PENDING' ? 'hourglass-empty' : 'error'} 
                          size={24} 
                          color={profile.approvalStatus === 'PENDING' ? '#FF9800' : '#F44336'} 
                        />
                        <Text style={[
                          styles.approvalStatusTitle,
                          { color: profile.approvalStatus === 'PENDING' ? '#FF9800' : '#F44336' }
                        ]}>
                          {profile.approvalStatus === 'PENDING' 
                            ? 'Pending Approval' 
                            : 'Application Rejected'}
                        </Text>
                      </View>
                      <Text style={[styles.approvalStatusText, { color: colors.textSecondary }]}>
                        {profile.approvalStatus === 'PENDING'
                          ? 'Your club application is being reviewed by our team. You will receive an email once your club is approved.'
                          : 'Unfortunately, your club application was not approved.'}
                      </Text>
                      {profile.approvalStatus === 'PENDING' && (
                        <Text style={[styles.approvalStatusText, { color: colors.textSecondary, marginTop: 4 }]}>
                          For approval inquiries or to submit your request directly, email chessmapsapp@gmail.com.
                        </Text>
                      )}
                      {profile.approvalStatus === 'REJECTED' && profile.rejectionReason && (
                        <View style={styles.rejectionReasonBox}>
                          <Text style={styles.rejectionReasonLabel}>Reason:</Text>
                          <Text style={styles.rejectionReasonText}>{profile.rejectionReason}</Text>
                        </View>
                      )}
                      {profile.submittedAt && (
                        <Text style={styles.submittedAtText}>
                          Submitted: {new Date(profile.submittedAt).toLocaleDateString()}
                        </Text>
                      )}
                      
                      {/* Resubmit Button */}
                      <TouchableOpacity
                        style={[styles.resubmitButton, resubmitting && styles.resubmitButtonDisabled]}
                        onPress={handleResubmitApplication}
                        disabled={resubmitting}
                      >
                        <Icon name="refresh" size={18} color={resubmitting ? '#999' : '#FF9800'} />
                        <Text style={[styles.resubmitButtonText, resubmitting && styles.resubmitButtonTextDisabled]}>
                          {resubmitting ? 'Resubmitting...' : 'Resubmit Application'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              )}

              {/* Tier Status */}
              <View style={dynamicStyles.section}>
                <View style={styles.infoRow}>
                  {profile.tier && (
                    <View style={[dynamicStyles.statusBadge, profile.tier === 'PRO' && dynamicStyles.statusBadgePro]}>
                      <Icon name="star" size={16} color={profile.tier === 'PRO' ? '#94EB98' : '#999'} />
                      <Text style={[styles.statusBadgeText, profile.tier === 'PRO' && styles.statusBadgeTextPro]}>
                        {profile.tier === 'PRO' ? 'Pro Tier' : 'Free Tier'}
                      </Text>
                    </View>
                  )}
                </View>
                {profile.claimedBy && profile.tier !== 'PRO' && (
                  <TouchableOpacity
                    style={styles.upgradeButton}
                    onPress={() => navigation.navigate('UpgradeClub', { clubId: profile.id })}
                  >
                    <Icon name="star" size={20} color="white" />
                    <Text style={styles.upgradeButtonText}>Upgrade to Pro</Text>
                  </TouchableOpacity>
                )}
              </View>

              {profile.address && (
                <View style={dynamicStyles.section}>
                  <View style={styles.infoRow}>
                    <Icon name="location-on" size={20} color={colors.textSecondary} />
                    <Text style={[styles.infoText, { color: colors.textSecondary }]}>{profile.address}</Text>
                  </View>
                </View>
              )}

              {profile.about && (
                <View style={dynamicStyles.section}>
                  <Text style={dynamicStyles.sectionTitle}>About</Text>
                  <Text style={[styles.aboutText, { color: colors.textSecondary }]}>{profile.about}</Text>
                </View>
              )}

              {/* Manage Your Club card + Post Composer + Posts — hidden until approved */}
              {!isClubPendingApproval && (
              <>
              {/* Manage Your Club card */}
              <TouchableOpacity
                style={[dynamicStyles.section, { marginBottom: 24 }]}
                onPress={() => navigation.navigate('YourClub')}
                activeOpacity={0.7}
              >
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: isDarkMode ? '#1E2D1E' : '#F0F8F0',
                  borderRadius: 14,
                  padding: 18,
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#2D4A2D' : '#D0E8D0',
                }}>
                  <View style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    backgroundColor: '#76AF87',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: 14,
                  }}>
                    <Icon name="dashboard" size={24} color="#fff" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 16, fontWeight: '700', color: colors.text }}>
                      Manage Your Club
                    </Text>
                    <Text style={{ fontSize: 13, color: colors.textSecondary, marginTop: 3 }}>
                      Events, members, tournaments, payments, and more
                    </Text>
                  </View>
                  <Icon name="chevron-right" size={24} color={colors.textSecondary} />
                </View>
              </TouchableOpacity>

              {/* Venue Photos */}
              {profile.approvalStatus === 'APPROVED' && profile.id && (
                <View style={dynamicStyles.section}>
                  <ClubHotspotPhotos clubId={profile.id} />
                </View>
              )}

              {/* Recent Instagram Posts Preview */}
              <View style={{ marginTop: 8 }}>
                <RecentInstagramPreview
                  clubId={profile.id}
                  navigation={navigation}
                  colors={colors}
                  isDarkMode={isDarkMode}
                />
              </View>

              {/* Edit Marker Location */}
              <View style={dynamicStyles.section}>
                <Text style={dynamicStyles.sectionTitle}>Map Marker</Text>
                <TouchableOpacity
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 12,
                    backgroundColor: isDarkMode ? '#1E2D1E' : '#F0F8F0',
                    borderRadius: 12,
                    padding: 16,
                    borderWidth: 1,
                    borderColor: isDarkMode ? '#2D4A2D' : '#D0E8D0',
                  }}
                  onPress={() => {
                    navigation.navigate('Map', { openLocationPicker: true });
                  }}
                  activeOpacity={0.7}
                >
                  <View style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    backgroundColor: '#76AF87',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                    <Icon name="edit-location-alt" size={22} color="#fff" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text }}>
                      Edit Marker Location
                    </Text>
                    <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                      {profile.latitude && profile.longitude
                        ? 'Tap to adjust where your club appears on the map'
                        : 'Set where your club marker appears on the map'}
                    </Text>
                  </View>
                  <Icon name="chevron-right" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>

              {/* Post Composer */}
              <PostComposer userId={profile.id} onPostCreated={handlePostCreated} />

              {/* Posts List */}
              {posts.length > 0 && (
                <View style={dynamicStyles.section}>
                  <Text style={dynamicStyles.sectionTitle}>Recent Announcements</Text>
                  {posts.map((post) => (
                    <View key={post.id} style={dynamicStyles.postCard}>
                      <Text style={[styles.postContent, { color: colors.text }]}>{post.content}</Text>
                      <Text style={[styles.postDate, { color: colors.textLight }]}>{formatDate(post.createdAt)}</Text>
                    </View>
                  ))}
                </View>
              )}
              </>
              )}

              {/* Notice shown when club is pending/rejected */}
              {isClubPendingApproval && (
                <View style={[dynamicStyles.section, { backgroundColor: '#FFF8E1', borderRadius: 12, padding: 20 }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <Icon name="lock" size={20} color="#F57F17" />
                    <Text style={{ fontSize: 15, fontWeight: '600', color: '#F57F17' }}>Features Locked</Text>
                  </View>
                  <Text style={{ fontSize: 14, color: '#5D4037', lineHeight: 20 }}>
                    Club management, events, posts, and profile editing are available once your club is approved. You can check your approval status above.
                  </Text>
                </View>
              )}
            </>
          )}

          {/* Admin Section */}
          {profile.isAdmin && (
            <View style={dynamicStyles.section}>
              <Text style={dynamicStyles.sectionTitle}>Admin Tools</Text>
              <TouchableOpacity
                style={dynamicStyles.adminButton}
                onPress={() => navigation.navigate('ManageHotspots')}
              >
                <Icon name="place" size={22} color="#FF9800" />
                <Text style={[styles.adminButtonText, { color: colors.text }]}>Manage Hotspots</Text>
                <Icon name="chevron-right" size={24} color={colors.textLight} />
              </TouchableOpacity>
              <TouchableOpacity
                style={dynamicStyles.adminButton}
                onPress={() => navigation.navigate('UnclaimedClubs')}
              >
                <Icon name="business" size={22} color="#FF9800" />
                <Text style={[styles.adminButtonText, { color: colors.text }]}>Unclaimed Clubs</Text>
                <Icon name="chevron-right" size={24} color={colors.textLight} />
              </TouchableOpacity>
              <TouchableOpacity
                style={dynamicStyles.adminButton}
                onPress={() => navigation.navigate('AdminClubApprovals')}
              >
                <Icon name="how-to-reg" size={22} color="#FF9800" />
                <Text style={[styles.adminButtonText, { color: colors.text }]}>Club Approvals</Text>
                <Icon name="chevron-right" size={24} color={colors.textLight} />
              </TouchableOpacity>
              <TouchableOpacity
                style={dynamicStyles.adminButton}
                onPress={() => navigation.navigate('AdminUsers')}
              >
                <Icon name="storage" size={22} color="#FF9800" />
                <Text style={[styles.adminButtonText, { color: colors.text }]}>Database (list / delete users)</Text>
                <Icon name="chevron-right" size={24} color={colors.textLight} />
              </TouchableOpacity>

              {/* Clubs on Pricing Page - visibility and list (collapsed by default) */}
              <View style={[dynamicStyles.adminButton, { flexDirection: 'column', alignItems: 'stretch', gap: 12 }]}>
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
                  onPress={() => setAdminClubsSectionExpanded((v) => !v)}
                  activeOpacity={0.7}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
                    <Icon name="groups" size={22} color="#FF9800" />
                    <Text style={[styles.adminButtonText, { color: colors.text }]}>Clubs on Pricing page</Text>
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Switch
                      value={socialProofVisible}
                      onValueChange={saveSocialProofVisible}
                      trackColor={{ false: '#ccc', true: '#94EB98' }}
                      thumbColor="#fff"
                    />
                    <Icon name={adminClubsSectionExpanded ? 'expand-less' : 'expand-more'} size={24} color={colors.textSecondary} />
                  </View>
                </TouchableOpacity>
                {adminClubsSectionExpanded && (
                  <>
                    <Text style={[styles.adminButtonText, { color: colors.textSecondary, fontSize: 13, fontWeight: '400' }]}>
                      Show &quot;Clubs using ChessMaps&quot; on Pricing page
                    </Text>
                    <TouchableOpacity
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}
                      onPress={openEditClubsModal}
                    >
                      <Icon name="edit" size={18} color="#FF9800" />
                      <Text style={{ color: '#FF9800', fontSize: 14, fontWeight: '600' }}>Edit clubs list</Text>
                      <Icon name="chevron-right" size={20} color={colors.textLight} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>
          )}

          {/* Edit Clubs Modal - only mount when open to avoid it appearing by default */}
          {showEditClubsModal && (
          <Modal visible={true} transparent animationType="fade">
            <Pressable style={styles.editClubsModalOverlay} onPress={() => closeEditClubsModal(false)}>
              <Pressable style={[styles.editClubsModalContent, { backgroundColor: colors.surface }]} onPress={(e) => e.stopPropagation()}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: colors.border }}>
                  <Text style={[styles.editClubsModalTitle, { color: colors.text }]}>Edit clubs list</Text>
                  <TouchableOpacity onPress={() => closeEditClubsModal(false)}>
                    <Icon name="close" size={24} color={colors.text} />
                  </TouchableOpacity>
                </View>
                <ScrollView style={{ maxHeight: 320 }} showsVerticalScrollIndicator>
                  {editClubsDraft.map((c, i) => (
                    <View key={i} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 12, backgroundColor: colors.backgroundDark, borderRadius: 8, marginBottom: 8 }}>
                      <Text style={{ color: colors.text, fontSize: 15 }}>{typeof c === 'string' ? c : c?.name || ''}</Text>
                      <TouchableOpacity onPress={() => removeDraftClub(i)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                        <Icon name="close" size={20} color={colors.textSecondary} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </ScrollView>
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
                  <TextInput
                    style={[styles.editClubsInput, { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundDark }]}
                    placeholder="New club name"
                    placeholderTextColor={colors.textLight}
                    value={newClubName}
                    onChangeText={setNewClubName}
                    onSubmitEditing={addDraftClub}
                  />
                  <TouchableOpacity style={[styles.editClubsAddBtn, { backgroundColor: colors.primary }]} onPress={addDraftClub}>
                    <Text style={styles.editClubsAddBtnText}>Add</Text>
                  </TouchableOpacity>
                </View>
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 20 }}>
                  <TouchableOpacity style={[styles.editClubsSaveBtn, { flex: 1, backgroundColor: colors.backgroundDark }]} onPress={() => closeEditClubsModal(false)}>
                    <Text style={[styles.editClubsSaveBtnText, { color: colors.text }]}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.editClubsSaveBtn, { flex: 1, backgroundColor: colors.primary }]} onPress={() => closeEditClubsModal(true)}>
                    <Text style={[styles.editClubsSaveBtnText, { color: '#fff' }]}>Save</Text>
                  </TouchableOpacity>
                </View>
              </Pressable>
            </Pressable>
          </Modal>
          )}

          {/* Action Buttons */}
          <View style={styles.actionButtonsContainer}>
            {/* Sign Out Button */}
            <TouchableOpacity style={styles.signOutButton} onPress={handleSignOut}>
              <Icon name="exit-to-app" size={20} color="white" />
              <Text style={styles.signOutButtonText}>Sign Out</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: 1,
    paddingVertical: 12,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  headerContentDesktop: {
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  settingsButton: {
    padding: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    marginBottom: 20,
  },
  content: {
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  contentDesktop: {
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  username: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 12,
  },
  email: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 4,
  },
  accountTypeBadge: {
    alignSelf: 'center',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
    marginTop: 8,
    marginBottom: 20,
  },
  accountTypeText: {
    fontSize: 14,
    fontWeight: '600',
  },
  debugBadge: {
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 4,
    marginBottom: 8,
  },
  debugText: {
    fontSize: 11,
    fontWeight: '500',
  },
  // Dashboard Styles
  dashboardContainer: {
    borderRadius: 20,
    padding: 20,
    marginBottom: 16,
  },
  dashboardRowDesktop: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
  },
  dashboardHeader: {
    marginBottom: 20,
  },
  dashboardTitle: {
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  dashboardSubtitle: {
    fontSize: 13,
    marginTop: 4,
  },
  mainStatCard: {
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    marginBottom: 16,
  },
  mainStatCardDesktop: {
    flex: 1,
    marginBottom: 0,
    justifyContent: 'center',
  },
  mainStatIcon: {
    marginBottom: 8,
  },
  mainStatValue: {
    fontSize: 48,
    fontWeight: '800',
    letterSpacing: -2,
  },
  mainStatLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginTop: 4,
  },
  secondaryStatsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  secondaryStatsRowDesktop: {
    flex: 1,
    flexDirection: 'column',
    gap: 12,
  },
  secondaryStatCard: {
    flex: 1,
    borderRadius: 14,
    padding: 16,
    alignItems: 'center',
  },
  secondaryStatValue: {
    fontSize: 24,
    fontWeight: '700',
    marginTop: 8,
    letterSpacing: -1,
  },
  secondaryStatLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  // Platform Cards
  platformCardsRowDesktop: {
    flexDirection: 'row',
    gap: 16,
  },
  platformCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    overflow: 'visible',
  },
  platformCardDesktop: {
    flex: 1,
    marginBottom: 0,
  },
  platformCardMobile: {
    paddingRight: 20,
  },
  platformCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  platformBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  platformBrandDesktop: {
    flex: 0,
  },
  platformCardHeaderActions: {
    flexDirection: 'row',
    gap: 8,
    flexShrink: 0,
  },
  platformIcon: {
    fontSize: 28,
  },
  platformName: {
    fontSize: 16,
    fontWeight: '600',
  },
  platformUserHandle: {
    fontSize: 12,
    marginTop: 2,
  },
  connectBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
  },
  connectBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  syncBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectInputContainer: {
    marginTop: 16,
  },
  connectInput: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
  },
  connectInputActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  connectConfirmBtn: {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  connectConfirmText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  connectCancelBtn: {
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  connectCancelText: {
    fontSize: 14,
    fontWeight: '500',
  },
  ratingCardsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
  },
  ratingMiniCard: {
    flex: 1,
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
  },
  ratingMiniValue: {
    fontSize: 20,
    fontWeight: '700',
    marginTop: 6,
    letterSpacing: -0.5,
  },
  ratingMiniLabel: {
    fontSize: 11,
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  syncTimestamp: {
    fontSize: 11,
    marginTop: 12,
    textAlign: 'center',
  },
  // Legacy styles kept for compatibility
  statsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  platformTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  ratingsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  ratingItem: {
    flex: 1,
    alignItems: 'center',
  },
  ratingLabel: {
    fontSize: 12,
  },
  ratingValue: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 4,
  },
  overallLabel: {
    fontSize: 14,
  },
  overallValue: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 4,
  },
  noDataText: {
    fontSize: 14,
    textAlign: 'center',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    fontSize: 16,
  },
  aboutText: {
    fontSize: 16,
    lineHeight: 24,
  },
  postContent: {
    fontSize: 14,
    lineHeight: 20,
  },
  postDate: {
    fontSize: 11,
    marginTop: 6,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  viewAllText: {
    fontSize: 14,
    color: '#94EB98',
    fontWeight: '600',
  },
  eventCardContent: {
    flex: 1,
  },
  eventCardTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 6,
  },
  eventCardDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  eventCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  eventCardText: {
    fontSize: 12,
  },
  adminButtonText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
  },
  editClubsModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  editClubsModalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 20,
  },
  editClubsModalTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  editClubsInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 15,
  },
  editClubsAddBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editClubsAddBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  editClubsSaveBtn: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editClubsSaveBtnText: {
    fontSize: 16,
    fontWeight: '600',
  },
  actionButtonsContainer: {
    marginTop: 24,
    marginBottom: 32,
    gap: 12,
  },
  actionSection: {
    marginTop: 12,
    marginBottom: 20,
  },
  pricingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  pricingButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  signOutButton: {
    backgroundColor: '#d32f2f',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  signOutButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FF9800',
  },
  statusBadgeTextPro: {
    color: '#2E7D32',
  },
  // Approval status styles
  approvalStatusContainer: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderLeftWidth: 4,
  },
  approvalStatusPending: {
    backgroundColor: '#FFF8E1',
    borderColor: '#FFCC02',
    borderLeftColor: '#FF9800',
  },
  approvalStatusRejected: {
    backgroundColor: '#FFEBEE',
    borderColor: '#FFCDD2',
    borderLeftColor: '#F44336',
  },
  approvalStatusApproved: {
    backgroundColor: '#E8F5E9',
    borderColor: '#C8E6C9',
    borderLeftColor: '#4CAF50',
  },
  approvalStatusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  approvalStatusTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  approvalStatusText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  rejectionReasonBox: {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  rejectionReasonLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#C62828',
    marginBottom: 4,
  },
  rejectionReasonText: {
    fontSize: 14,
    color: '#C62828',
    lineHeight: 20,
  },
  submittedAtText: {
    fontSize: 12,
    color: '#999',
    marginTop: 8,
  },
  resubmitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FFF3E0',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#FFCC02',
  },
  resubmitButtonDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#E0E0E0',
  },
  resubmitButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FF9800',
  },
  resubmitButtonTextDisabled: {
    color: '#999',
  },
  claimButton: {
    backgroundColor: '#FF9800',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 12,
  },
  claimButtonDisabled: {
    backgroundColor: '#ccc',
  },
  claimButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  upgradeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
  },
  upgradeButtonText: {
    color: '#333',
    fontSize: 14,
    fontWeight: '600',
  },
  importSubtitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  platformContainer: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  platformHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  platformTitleRow: {
    flex: 1,
  },
  platformUsername: {
    fontSize: 12,
    marginTop: 4,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  linkButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  syncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  syncButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  inputContainer: {
    marginTop: 12,
  },
  usernameInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 8,
  },
  inputButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  confirmButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  cancelButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  ratingsDisplay: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  lastSyncText: {
    fontSize: 11,
    marginTop: 8,
    textAlign: 'center',
  },
  onlineStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 16,
  },
  onlineIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  onlineStatusText: {
    fontSize: 13,
    fontWeight: '500',
  },
  profileFieldContainer: {
    marginBottom: 0,
  },
  profileFieldHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  profileFieldLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  profileFieldLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  editFieldButton: {
    padding: 6,
  },
  profileFieldValue: {
    fontSize: 16,
    fontStyle: 'italic',
  },
  statusValue: {
    lineHeight: 22,
  },
  editFieldContainer: {
    marginTop: 4,
  },
  fieldInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  statusInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  characterCount: {
    fontSize: 11,
    textAlign: 'right',
    marginTop: 4,
  },
  editFieldButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  saveFieldButton: {
    flex: 1,
    padding: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveFieldButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  cancelFieldButton: {
    flex: 1,
    padding: 10,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  cancelFieldButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  // Friends section styles
  friendsSection: {
    padding: 0,
    overflow: 'hidden',
  },
  friendsSectionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  friendsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  friendsIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  friendsTextContainer: {
    gap: 2,
  },
  friendsTitle: {
    fontSize: 17,
    fontWeight: '600',
  },
  friendsSubtitle: {
    fontSize: 14,
  },
  friendsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pendingBadge: {
    backgroundColor: '#94EB98',
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  pendingBadgeText: {
    color: '#1A1A1A',
    fontSize: 13,
    fontWeight: '700',
  },
  pendingRequestsBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
  },
  pendingRequestsText: {
    fontSize: 13,
    fontWeight: '600',
  },
  // Club Management Styles
  clubManagementTabs: {
    flexDirection: 'row',
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.05)',
    padding: 4,
    marginBottom: 16,
  },
  clubManagementTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    gap: 4,
  },
  clubManagementTabActive: {
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  clubManagementTabText: {
    fontSize: 12,
    fontWeight: '500',
  },
  clubManagementTabTextActive: {
    fontWeight: '600',
  },
  clubTabContent: {
    minHeight: 200,
  },
  clubTabLoading: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  clubTabEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    gap: 8,
  },
  clubTabEmptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 8,
  },
  clubTabEmptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  tierLimitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    gap: 8,
  },
  tierLimitText: {
    flex: 1,
    fontSize: 13,
    color: '#FF9800',
  },
  tierUpgradeButton: {
    backgroundColor: '#FF9800',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  tierUpgradeButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  clubCreateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#94EB98',
    padding: 14,
    borderRadius: 10,
    marginTop: 12,
    gap: 8,
  },
  clubCreateButtonDisabled: {
    backgroundColor: '#e0e0e0',
  },
  clubCreateButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  clubCreateButtonTextDisabled: {
    color: '#999',
  },
  eventTypeTag: {
    backgroundColor: 'rgba(148, 235, 152, 0.2)',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  eventTypeTagText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#4CAF50',
    textTransform: 'uppercase',
  },
  // Members section styles
  memberHeaderSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
    marginBottom: 8,
  },
  memberCountContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  memberCountText: {
    fontSize: 18,
    fontWeight: '600',
  },
  visibilityToggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  visibilityLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  visibilityHelpText: {
    fontSize: 12,
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  membersList: {
    gap: 8,
  },
  memberCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  memberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#94EB98',
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e0e0e0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberDetails: {
    flex: 1,
  },
  memberName: {
    fontSize: 15,
    fontWeight: '600',
  },
  memberJoinDate: {
    fontSize: 12,
    marginTop: 2,
  },
  removeMemberButton: {
    padding: 8,
  },
  // Pro feature banner styles
  proFeatureBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 179, 0, 0.1)',
    padding: 14,
    borderRadius: 10,
    marginBottom: 16,
    gap: 12,
  },
  proFeatureBannerContent: {
    flex: 1,
  },
  proFeatureBannerTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#333',
    marginBottom: 4,
  },
  proFeatureBannerText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
  },
  proFeatureButton: {
    backgroundColor: '#FFB300',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  proFeatureButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
});

export default ProfileScreen;
